import clsx from 'clsx'
import React from 'react'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import FormGroup from '@mui/material/FormGroup'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import ApplicationLogo from 'src/components/icons/ApplicationLogo'
import Colors from 'src/utils/views/colors'

import { Alert, CircularProgress, IconButton, InputAdornment } from '@mui/material'
import { Logger } from 'wdc-cube'
import { ChangePasswordScope } from '../Main.scopes'

const LOG = Logger.get('ChangePasswordView')

export const usseStyles = makeStyles()({
    view: {
        display: 'flex',
        flexDirection: 'row',
        alignSelf: 'stretch',
        flexGrow: 1,
        justifyContent: 'center'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'center'
    },
    logo: {
        width: 'unset',
        height: '64px',
        marginBottom: '32px'
    },
    card: {
        minWidth: '472px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        rowGap: '8px'
    },
    title: {
        fontSize: '1.125rem',
        fontWeight: 500,
        lineHeight: 1.25,
        alignSelf: 'center',
        textAlign: 'center',
        marginBottom: '16px'
    }
})

export type ChangePasswordViewProps = {
    className?: string
    scope: ChangePasswordScope
}

export function ChangePasswordView({ className, scope }: ChangePasswordViewProps) {
    bindUpdate(React, scope)

    const emitChangePassword = () => {
        scope.onChangePassword().catch(LOG.caught)
    }

    const handleClickShowPassword = () => {
        scope.showPassword = !scope.showPassword
        scope.forceUpdate()
    }

    const handleClickShowPasswordConfirmation = () => {
        scope.showPasswordConfirmation = !scope.showPasswordConfirmation
        scope.forceUpdate()
    }

    const handlePasswordChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => (scope.password = e.target.value),
        [scope]
    )

    const handlePasswordConfirmationChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => (scope.passwordConfirmation = e.target.value),
        [scope]
    )

    const { classes } = usseStyles()

    return (
        <div className={clsx(classes.view, className)}>
            <div className={classes.content}>
                <ApplicationLogo className={classes.logo} place="sign-in" textColor={Colors.primary.main} />
                <Card className={classes.card}>
                    <Typography variant="h1" className={classes.title}>
                        Redefinir senha
                    </Typography>
                    <TextField
                        label="Senha"
                        type={scope.showPassword ? 'text' : 'password'}
                        onChange={handlePasswordChanged}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {scope.showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                    <TextField
                        label="Confirmação de senha"
                        type={scope.showPasswordConfirmation ? 'text' : 'password'}
                        onChange={handlePasswordConfirmationChanged}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPasswordConfirmation}
                                            edge="end"
                                        >
                                            {scope.showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                    {scope.passwordError && <Alert severity="warning">{scope.passwordError}</Alert>}

                    <FormGroup>
                        <Button variant="contained" disabled={!!scope.waiting} onClick={emitChangePassword}>
                            {scope.waiting && <CircularProgress size={15} />}
                            Redefinir senha
                        </Button>
                        <Button variant="text" onClick={scope.onBackToLogin}>
                            Voltar ao login
                        </Button>
                    </FormGroup>
                </Card>
            </div>
        </div>
    )
}
